import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { CalendarView, Booking } from '@/types';
import { calculateDateRange, isBookingInRange } from '@/utils/date-range-utils';
import { bookingsApi, Booking as ApiBooking } from '@/services/api';
import { useCompany } from '@/contexts/CompanyContext';
import { perfStart } from '@/utils/perf-log';

type DataMode = 'demo' | 'live';

const RANGE_CACHE_TTL_MS = 60_000;
const RANGE_FETCH_DEBOUNCE_MS = 200;

const rangeKey = (
  companyId: string | undefined,
  start: Date,
  end: Date,
): string => `${companyId ?? ''}|${start.toISOString()}|${end.toISOString()}`;

interface UseDateRangeBookingsOptions {
  calendarView: CalendarView;
  selectedDate: Date;
  allBookings: Booking[];
  mode: DataMode;
  enabled?: boolean;
  /**
   * Bumping this re-runs the date-range fetch for the current range, so a
   * caller can invalidate the cached bookings after they were changed
   * elsewhere (e.g. a conflict resolution committed on the report).
   */
  refreshKey?: number;
}

interface UseDateRangeBookingsResult {
  bookings: Booking[];
  isLoading: boolean;
  error: string | null;
  dateRange: { startDate: Date; endDate: Date };
  /** Re-reads the visible range; settles once the rows it read are in the cache. */
  refetchLiveBookings: () => Promise<void>;
  removeBookingFromCache: (id: string) => void;
}

const safeJsonParse = (val: unknown): Record<string, unknown> | undefined => {
  if (!val) return undefined;
  if (typeof val === 'object') return val as Record<string, unknown>;
  try { return JSON.parse(val as string) as Record<string, unknown>; } catch { return undefined; }
};

export const mapApiBookingToBooking = (apiBooking: ApiBooking): Booking => ({
  id: String(apiBooking.id),
  resourceId: apiBooking.resourceId != null ? String(apiBooking.resourceId) : 'unallocated',
  resourceIds: apiBooking.resourceIds
    ? (safeJsonParse(apiBooking.resourceIds) as unknown as string[] | undefined)?.map?.(String)
    : undefined,
  projectId: apiBooking.projectId != null ? String(apiBooking.projectId) : null,
  projectName: apiBooking.projectName || '',
  costCentreId: apiBooking.costCentreId != null ? Number(apiBooking.costCentreId) : null,
  clientId: apiBooking.clientId != null ? Number(apiBooking.clientId) : null,
  description: apiBooking.description,
  startTime: apiBooking.startTime,
  endTime: apiBooking.endTime,
  timezone: apiBooking.timezone,
  color: apiBooking.color,
  durationType: apiBooking.durationType as Booking['durationType'],
  halfDayPeriod: apiBooking.halfDayPeriod as Booking['halfDayPeriod'],
  billingClassification: apiBooking.billingClassification as Booking['billingClassification'],
  workType: apiBooking.workType as Booking['workType'],
  taskDescription: apiBooking.taskDescription,
  bookingNotes: apiBooking.bookingNotes,
  status: apiBooking.status as Booking['status'],
  priority: apiBooking.priority as Booking['priority'],
  notifyReminders: apiBooking.notifyReminders,
  bookingTypeId: apiBooking.bookingTypeId != null ? String(apiBooking.bookingTypeId) : undefined,
  customer: apiBooking.customer,
  location: apiBooking.location,
  addressLine2: apiBooking.addressLine2,
  city: apiBooking.city,
  county: apiBooking.county,
  postcode: apiBooking.postcode,
  country: apiBooking.country,
  details: apiBooking.details,
  // Recurring booking fields
  recurrence: safeJsonParse(apiBooking.recurrence) as Booking['recurrence'],
  recurrenceGroupId: apiBooking.recurrenceGroupId || undefined,
  isRecurrenceParent: apiBooking.isRecurrenceParent || undefined,
  recurrencePending: apiBooking.recurrencePending || undefined,
  demandPlanGroupId: apiBooking.demandPlanGroupId || undefined,
  autoAdjustGroupId: apiBooking.autoAdjustGroupId || undefined,
  autoAdjustGroupStartTime: apiBooking.autoAdjustGroupStartTime || undefined,
  autoAdjustGroupEndTime: apiBooking.autoAdjustGroupEndTime || undefined,
  autoAdjustGroupPieceCount: apiBooking.autoAdjustGroupPieceCount ?? undefined,
  demandId: apiBooking.demandId != null ? Number(apiBooking.demandId) : undefined,
  demandLineId: apiBooking.demandLineId != null ? Number(apiBooking.demandLineId) : undefined,
  source: apiBooking.source || undefined,
  // Additional custom fields
  costCentreTask: apiBooking.costCentreTask,
  bookingReference: apiBooking.bookingReference,
  userChar: safeJsonParse(apiBooking.userChar) as Booking['userChar'],
  userNumber: safeJsonParse(apiBooking.userNumber) as Booking['userNumber'],
  userFlag: safeJsonParse(apiBooking.userFlag) as Booking['userFlag'],
  userDate: safeJsonParse(apiBooking.userDate) as Booking['userDate'],
  userNotes: safeJsonParse(apiBooking.userNotes) as Booking['userNotes'],
  userTime: safeJsonParse(apiBooking.userTime) as Booking['userTime'],
  requiredSkills: safeJsonParse(apiBooking.requiredSkills) as string[] | undefined,
  requiredRole: apiBooking.requiredRole || undefined,
  resourceAssignments: safeJsonParse(apiBooking.resourceAssignments) as Booking['resourceAssignments'],
  companyId: apiBooking.companyId || undefined,
  durationHours: apiBooking.durationHours,
  durationDays: apiBooking.durationDays,
});

export function useDateRangeBookings({
  calendarView,
  selectedDate,
  allBookings,
  mode,
  enabled = true,
  refreshKey = 0,
}: UseDateRangeBookingsOptions): UseDateRangeBookingsResult {
  const [liveBookings, setLiveBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Key of the range `liveBookings` currently holds, so a range that has been
  // asked for but not yet answered reads as loading from its first render —
  // through the debounce and before the fetch effect has run.
  const [loadedRangeKey, setLoadedRangeKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fetchIdRef = useRef(0);
  const rangeCacheRef = useRef(new Map<string, { bookings: Booking[]; fetchedAt: number }>());
  const hasFetchedRef = useRef(false);

  const dateRange = useMemo(
    () => calculateDateRange(calendarView, selectedDate),
    [calendarView, selectedDate]
  );

  // Get the selected company ID from the company dropdown so it can be
  // passed as a query parameter to the bookings date-range API.
  const { currentCompany, isLoading: isCompanyLoading } = useCompany();
  const companyIdRef = useRef(currentCompany?.id);
  companyIdRef.current = currentCompany?.id;

  const cancelInFlight = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    fetchIdRef.current++;
  }, []);

  const fetchLiveBookings = useCallback(async (start: Date, end: Date) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const currentFetchId = ++fetchIdRef.current;
    const companyId = companyIdRef.current;
    hasFetchedRef.current = true;

    setIsLoading(true);
    setError(null);

    try {
      const startISO = start.toISOString();
      const endISO = end.toISOString();
      const endFetch = perfStart('bookings date-range fetch');
      const apiBookings = await bookingsApi.getByDateRange(startISO, endISO, false, companyId);
      endFetch({ bookings: apiBookings.length, startISO, endISO });

      // A read this one superseded may still answer after it: only the newest read
      // stands for the range, or an older answer puts back what has since been saved.
      if (fetchIdRef.current !== currentFetchId) return;

      const mapped = apiBookings.map(mapApiBookingToBooking);
      const key = rangeKey(companyId, start, end);
      rangeCacheRef.current.set(key, {
        bookings: mapped,
        fetchedAt: Date.now(),
      });
      setLiveBookings(mapped);
      setLoadedRangeKey(key);
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (fetchIdRef.current !== currentFetchId) return;
      const message = err instanceof Error ? err.message : 'Failed to fetch bookings for date range';
      console.error('Error fetching date-range bookings:', message);
      setError(message);
      setLiveBookings([]);
      setLoadedRangeKey(rangeKey(companyId, start, end));
    } finally {
      // Only clear loading if this is still the latest fetch.
      // A superseded fetch's finally must not reset the flag while a
      // newer fetch is still in flight.
      if (fetchIdRef.current === currentFetchId) {
        setIsLoading(false);
      }
    }
  }, []);

  const previousRefreshKeyRef = useRef(refreshKey);
  const previousCompanyIdRef = useRef(currentCompany?.id);
  useEffect(() => {
    if (previousRefreshKeyRef.current !== refreshKey ||
      previousCompanyIdRef.current !== currentCompany?.id) {
      rangeCacheRef.current.clear();
    }
    previousRefreshKeyRef.current = refreshKey;
    previousCompanyIdRef.current = currentCompany?.id;
  }, [refreshKey, currentCompany?.id]);

  useEffect(() => {
    // Wait until the owning screen and company context have resolved their
    // initial state before requesting the final date range.
    if (!enabled || isCompanyLoading || !currentCompany?.id) return;

    if (mode !== 'live') return;

    const key = rangeKey(currentCompany.id, dateRange.startDate, dateRange.endDate);
    const cached = rangeCacheRef.current.get(key);
    if (cached && Date.now() - cached.fetchedAt < RANGE_CACHE_TTL_MS) {
      cancelInFlight();
      setLiveBookings(cached.bookings);
      setLoadedRangeKey(key);
      setError(null);
      setIsLoading(false);
      return;
    }

    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    if (!hasFetchedRef.current) {
      fetchLiveBookings(dateRange.startDate, dateRange.endDate);
    } else {
      debounceTimer = setTimeout(() => {
        fetchLiveBookings(dateRange.startDate, dateRange.endDate);
      }, RANGE_FETCH_DEBOUNCE_MS);
    }

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      cancelInFlight();
    };
    // Deliberately keyed on the date range, mode and company only: bookings created
    // locally (a single create, or a recurring series still being written in the
    // background) are merged in below, so they must not trigger another date-range
    // request. The timeline reloads when the user moves the range or changes
    // view, and whenever the caller invalidates via `refreshKey`.
  }, [mode, dateRange.startDate, dateRange.endDate, fetchLiveBookings, cancelInFlight, currentCompany?.id, isCompanyLoading, enabled, refreshKey]);

  const refetchLiveBookings = useCallback(async () => {
    if (enabled && mode === 'live') {
      rangeCacheRef.current.clear();
      await fetchLiveBookings(dateRange.startDate, dateRange.endDate);
    }
  }, [mode, dateRange.startDate, dateRange.endDate, fetchLiveBookings, enabled]);

  const demoBookings = useMemo(() => {
    if (mode !== 'demo') return [];

    return allBookings.filter((booking) =>
      isBookingInRange(
        booking.startTime,
        booking.endTime,
        dateRange.startDate,
        dateRange.endDate
      )
    );
  }, [mode, allBookings, dateRange.startDate, dateRange.endDate]);

  // Remove a booking from the hook's local cache so it disappears from
  // the timeline immediately without waiting for an API refetch.
  const removeBookingFromCache = useCallback((id: string) => {
    setLiveBookings(prev => prev.filter(b => String(b.id) !== String(id)));
    rangeCacheRef.current.forEach((entry, key) => {
      const bookings = entry.bookings.filter(b => String(b.id) !== String(id));
      if (bookings.length !== entry.bookings.length) {
        rangeCacheRef.current.set(key, { ...entry, bookings });
      }
    });
  }, []);

  // For live mode, merge API-fetched bookings with any newly created bookings
  // from the context that haven't been fetched yet. This ensures bookings
  // appear immediately on the timeline after creation without waiting for
  // the next date-range API call.
  // Context bookings carry optimistic updates (e.g. after drag-drop or edit)
  // so we overlay them onto API-fetched data to keep utilisation and display
  // in sync before the next refetch completes.
  const mergedLiveBookings = useMemo(() => {
    if (mode !== 'live') return [];

    // Build a lookup of context bookings so we can merge optimistic updates
    const contextMap = new Map<string, Booking>();
    for (const b of allBookings) {
      contextMap.set(String(b.id), b);
    }

    const fetchedIds = new Set(liveBookings.map(b => String(b.id)));

    // For API-fetched bookings, overlay the context version on top so that
    // optimistic updates (changed times, resource, status, etc.) are
    // reflected immediately. API fields act as defaults for anything the
    // context doesn't carry.
    const mergedFetched = liveBookings.map(b => {
        const ctx = contextMap.get(String(b.id));
        if (ctx) {
          return { ...b, ...ctx };
        }
        return b;
      });

    const newContextBookings = allBookings.filter((booking) => {
      const id = String(booking.id);
      if (fetchedIds.has(id)) return false;
      return isBookingInRange(
        booking.startTime,
        booking.endTime,
        dateRange.startDate,
        dateRange.endDate
      );
    });

    return [...mergedFetched, ...newContextBookings];
  }, [mode, liveBookings, allBookings, dateRange.startDate, dateRange.endDate]);

  const bookings = mode === 'demo' ? demoBookings : mergedLiveBookings;

  const isAwaitingRange = mode === 'live'
    && enabled
    && !isCompanyLoading
    && !!currentCompany?.id
    && loadedRangeKey !== rangeKey(currentCompany.id, dateRange.startDate, dateRange.endDate);

  return {
    bookings,
    isLoading: isLoading || isAwaitingRange,
    error,
    dateRange,
    refetchLiveBookings,
    removeBookingFromCache,
  };
}
